import { useEffect } from "react";
import { CLAIM_HOLD_CHOICES_MS } from "@gooner/shared";
import type { ScreenSettings } from "../lib/screenSettings";
import { Button, Toggle, cx } from "../control/ui/primitives";
import { holdCountdown, useCountdown } from "../lib/useCountdown";

/** How each hold choice reads on the glass. Keyed by the shared list's own values, so a
 *  choice added there without a label here fails to compile rather than rendering blank. */
const HOLD_LABELS: Record<number, string> = {
  0: "Off",
  [5 * 60 * 1000]: "5 minutes",
  [10 * 60 * 1000]: "10 minutes",
};

/**
 * A screen's own settings, opened from the gear in its bar.
 *
 * Cut to ConfirmDialog's bones — same scrim, same panel, same Escape — but sized for a
 * TELEVISION. The type is `clamp()`d against the viewport rather than stepped at `sm:`,
 * for the reason the code card records: `sm:` cannot tell a laptop from a 55" screen, and
 * this is a dialog somebody reads and presses from across a room.
 *
 * ESCAPE CLOSES, ENTER DOES NOTHING. ConfirmDialog binds Enter because it is a two-button
 * decision with a default answer. There is no decision here — every control commits the
 * moment it is touched — so an Enter that dismissed the dialog would be a keystroke that
 * looks like confirmation and is really just an exit.
 *
 * Presentational: every action is a prop, so it renders standalone with no socket, no
 * router and no session, exactly like ScreenControls.
 */
export function ScreenSettingsDialog({
  settings,
  onChange,
  heldUntil,
  onRelease,
  onClose,
}: {
  settings: ScreenSettings;
  onChange: (next: ScreenSettings) => void;
  /** A local deadline while somebody holds this screen, or null when it is free. */
  heldUntil: number | null;
  onRelease: () => void;
  onClose: () => void;
}) {
  const left = useCountdown(heldUntil);
  const held = left > 0;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-[clamp(1.25rem,2vw,2rem)] shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-label="Screen settings"
      >
        <h3 className="text-[clamp(1rem,1.6vw,1.5rem)] font-semibold text-zinc-100">
          Screen settings
        </h3>

        {/* PARTY QR. The explanation is not optional decoration — this switch hands the
            television to anybody who can point a camera at it, and somebody flipping it
            at a party deserves to be told that in the same breath. */}
        <div className="mt-[clamp(1rem,1.5vw,1.75rem)]">
          <Toggle
            checked={settings.partyQr}
            onChange={(partyQr) => onChange({ ...settings, partyQr })}
            label="Show party screen QR code"
            title="Show party screen QR code — anyone who scans it takes control of this screen"
          />
          <p className="mt-1.5 text-[clamp(0.6875rem,1vw,0.9375rem)] leading-relaxed text-zinc-500">
            Puts a code in the corner of this screen. Anyone who scans it with their phone
            camera takes control.
          </p>
        </div>

        {/* THE HOLD. Segmented rather than a <Select>: three fixed choices, and a dropdown
            on a television is a menu somebody has to steer a cursor into. */}
        <div className="mt-[clamp(1rem,1.5vw,1.75rem)]">
          <span className="text-[clamp(0.6875rem,0.9vw,0.875rem)] font-medium uppercase tracking-wide text-zinc-400">
            Claim timer
          </span>
          <div className="mt-2 flex gap-2" role="group" aria-label="Claim timer">
            {CLAIM_HOLD_CHOICES_MS.map((ms) => (
              <button
                key={ms}
                type="button"
                aria-pressed={settings.holdMs === ms}
                onClick={() => onChange({ ...settings, holdMs: ms })}
                className={cx(
                  "flex-1 rounded-lg border px-3 py-2 text-[clamp(0.8125rem,1.1vw,1.0625rem)] transition-colors",
                  settings.holdMs === ms
                    ? "border-[var(--color-accent)]/60 bg-[var(--color-accent)]/15 font-semibold text-zinc-100"
                    : "border-[var(--color-edge)] bg-[var(--color-ink)] text-zinc-400 hover:text-zinc-200",
                )}
              >
                {HOLD_LABELS[ms]}
              </button>
            ))}
          </div>
          <p className="mt-1.5 text-[clamp(0.6875rem,1vw,0.9375rem)] leading-relaxed text-zinc-500">
            How long someone keeps this screen after they claim it, before anyone else can.
          </p>
        </div>

        {/* RELEASE, only while there is something to release. This is the ONLY override
            the hold has, and it is here rather than on the remote on purpose: whoever is
            standing at the television is the one person who can see the room. */}
        {held ? (
          <div className="mt-[clamp(1rem,1.5vw,1.75rem)] flex items-center justify-between gap-3 rounded-lg border border-amber-400/30 bg-amber-400/10 px-3 py-2.5">
            <span className="text-[clamp(0.75rem,1vw,1rem)] tabular-nums text-amber-200">
              Taken for another {holdCountdown(left)}
            </span>
            <Button variant="danger" onClick={onRelease}>
              Release now
            </Button>
          </div>
        ) : null}

        <div className="mt-[clamp(1.25rem,2vw,2rem)] flex justify-end">
          <Button variant="ghost" onClick={onClose}>
            Done
          </Button>
        </div>
      </div>
    </div>
  );
}
